import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LogIn, LogOut, Coffee, Play, Clock, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { usePresenceSession } from '../../hooks/usePresenceSession';

/**
 * TopBar presence/shift indicator pill with quick-action popover.
 *
 * Purpose:
 *   Displays the user's live shift status (`In servizio`, `In pausa`, `Fuori servizio`)
 *   in the global navbar alongside `SessionTimerBadge` and allows 1-click clock-in,
 *   break toggle, or clock-out from any page in the application.
 *
 * Inputs:
 *   None (reads authenticated user and presence session via `usePresenceSession`).
 *
 * Outputs:
 *   JSX badge + interactive popover, or `null` when unauthenticated.
 *
 * Side effects:
 *   Registers document click-outside and `Escape` listeners while the popover is open.
 */
export default function PresenceStatusBadge() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const {
    loading,
    busy,
    inServizio,
    inPausa,
    statusTone,
    statusText,
    shortStatusText,
    clockIn,
    togglePausa,
    clockOut,
  } = usePresenceSession();

  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!isAuthenticated) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Stato turno: ${statusText}. Clicca per timbrare`}
        title={`${statusText} (clicca per gestire il turno)`}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors cursor-pointer"
        style={{
          backgroundColor: statusTone.bg,
          color: statusTone.text,
          borderColor: statusTone.border,
        }}
      >
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: statusTone.dot }}
          aria-hidden="true"
        />
        <span className="hidden md:inline tabular-nums whitespace-nowrap">
          {shortStatusText}
        </span>
        <span className="hidden sm:inline md:hidden whitespace-nowrap">
          {loading ? 'Turno...' : inPausa ? 'In pausa' : inServizio ? 'In servizio' : 'Fuori servizio'}
        </span>
        <Clock size={13} strokeWidth={1.85} className="sm:hidden shrink-0" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Gestione turno giornaliero"
          className="fixed left-3 right-3 top-[calc(env(safe-area-inset-top,0px)+4.25rem)] w-auto max-w-sm mx-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72 sm:max-w-none sm:mx-0 rounded-lg border p-3.5 z-50 space-y-3"
          style={{
            backgroundColor: 'var(--wfm-bg-white)',
            borderColor: 'var(--wfm-border)',
            boxShadow: 'var(--wfm-shadow-lg)',
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="wfm-tiny">Stato turno giornaliero</p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: statusTone.dot }}
                  aria-hidden="true"
                />
                <p
                  className="text-[13px] font-bold leading-snug truncate tabular-nums"
                  style={{ color: 'var(--wfm-text-main)' }}
                >
                  {statusText}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!inServizio && (
              <button
                type="button"
                onClick={async () => {
                  const ok = await clockIn();
                  if (ok) setOpen(false);
                }}
                disabled={loading || busy}
                className="wfm-btn-success wfm-btn-sm w-full justify-center"
              >
                <LogIn size={14} strokeWidth={2} />
                <span>Registra entrata</span>
              </button>
            )}

            {inServizio && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await togglePausa();
                    if (ok) setOpen(false);
                  }}
                  disabled={busy}
                  className={`${inPausa ? 'wfm-btn-success' : 'wfm-btn-warning'} wfm-btn-sm flex-1 justify-center`}
                >
                  {inPausa ? (
                    <>
                      <Play size={14} strokeWidth={2} />
                      <span>Riprendi</span>
                    </>
                  ) : (
                    <>
                      <Coffee size={14} strokeWidth={2} />
                      <span>Pausa</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await clockOut();
                    if (ok) setOpen(false);
                  }}
                  disabled={busy}
                  className="wfm-btn-danger wfm-btn-sm flex-1 justify-center"
                >
                  <LogOut size={14} strokeWidth={2} />
                  <span>Uscita</span>
                </button>
              </>
            )}
          </div>

          <div
            className="pt-2.5 border-t flex items-center justify-between"
            style={{ borderColor: 'var(--wfm-border)' }}
          >
            <Link
              to="/workforce/presenze"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold no-underline flex items-center gap-1 transition-colors hover:underline"
              style={{ color: 'var(--wfm-primary-ink)' }}
            >
              <span>Vai al registro presenze</span>
              <ArrowRight size={13} strokeWidth={2} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
